import { Divider } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import { ServerImageInfo, ServerScaleSpecInfo } from '@/api/type-server.ts'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Loading, message, Trans } from '@/components'
import { formatRamMB } from '@/utils/formatter.ts'

interface ScalingSpecDetailProp {
  scalingSpecResId: string
}

export default function ScalingSpecTarget({ scalingSpecResId }: ScalingSpecDetailProp) {
  const [loading, setLoading] = useState(false)
  const [scaleSpec, setScaleSpec] = useState<ServerScaleSpecInfo>()
  const [imageSpec, setImageSpec] = useState<ServerImageInfo>()
  const [state, copyToClipboard] = useCopyToClipboard()

  const getScaleSpec = useCallback(
    async (specId: string) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpec({
          spec_id: specId,
        })
        if (res.code === 0) {
          setScaleSpec(res.spec)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId],
  )

  const getImages = useCallback(
    async (imageId: number) => {
      setLoading(true)
      try {
        const res = await API.getImageList({
          page: 1,
          per_page: 99,
        })
        if (res.code === 0) {
          // find image by id
          const image = res.images.find((im) => im.id === imageId)
          setImageSpec(image)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId],
  )

  useEffect(() => {
    getScaleSpec(scalingSpecResId)
  }, [getScaleSpec, scalingSpecResId])

  useEffect(() => {
    if (scaleSpec) {
      getImages(scaleSpec.runtimeEnvs[0].systemEnv.fromImageId)
    }
  }, [getImages, scaleSpec])

  useEffect(() => {
    if (state.value) {
      message.success('Copied')
    }
  }, [state])

  return (
    <>
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && scaleSpec && (
        <div className="p-4 bg-panel-gray-100 rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="scaling.scalingTarget.instancePreview">Instance Preview</Trans>
            </div>
            <Divider className="my-0" />
          </div>

          <div className="grid grid-cols-4 gap-y-4 bg-panel-gray-100 rounded-lg">
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.image">Image</Trans>
                </div>
                <div className="t-sb text-primary">{imageSpec?.alias}</div>
              </div>
              <div className="col-span-3">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.imageDesc">Image description</Trans>
                </div>
                <div className="t-sb text-primary">{imageSpec?.description}</div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.gpuModel">GPU Model</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpuName}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.gpus">GPUs</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpus}
                </div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.gpuRam">GPU RAM</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpuRamMB)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.cpuModel">CPU Model</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.cpuName}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.vCpus">vCPUs</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.cpus}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.ram">RAM</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(scaleSpec.runtimeEnvs[0].hardwareEnv.resource.ramMB)}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.storage">Storage</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.diskGB} GB
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="scaling.scalingTarget.serviceAndStartCommand">
                Service & Start Command
              </Trans>
            </div>
            <Divider className="my-0" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 bg-panel-gray-100 rounded-lg">
            <div className="col-span-4 grid grid-cols-4 space-x-2">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.protocol">Protocol</Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.length > 0 ? 'Https' : '-'}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.port">Port</Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.length > 0 ? (
                    <>
                      <div>{scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.join(',')}</div>
                      <div
                        className="cursor-pointer flex space-x-0.5 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.join(','))
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <div>-</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scaling.scalingTarget.cmd">Start Command</Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {scaleSpec.runtimeEnvs[0].systemEnv.cmd.length > 0 ? (
                    <>
                      <div>{scaleSpec.runtimeEnvs[0].systemEnv.cmd}</div>
                      <div
                        className="cursor-pointer flex space-x-0.5 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(scaleSpec.runtimeEnvs[0].systemEnv.cmd)
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
